// Datetime utilities: ensure consistent timezone handling across the app

/**
 * UTC 시간값(ISO/Date/epoch)을 받아 RFC3339(타임존 오프셋 포함) 문자열로 변환합니다.
 * - 유효하지 않은 값은 null을 반환합니다.
 * @param {string|number|Date} value UTC 기준 시간값 (ISO 문자열, Date, epoch ms)
 * @returns {string|null} RFC3339 형태의 문자열 (예: 2025-01-15T13:45:00+09:00) 또는 null
 */

export const toRfc3339OrNull = (value) => {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  const pad = (n) => String(n).padStart(2, '0')
  const y = date.getFullYear()
  const m = pad(date.getMonth() + 1)
  const d = pad(date.getDate())
  const hh = pad(date.getHours())
  const mm = pad(date.getMinutes())
  const ss = pad(date.getSeconds())
  const tz = -date.getTimezoneOffset()
  const sign = tz >= 0 ? '+' : '-'
  const tzh = pad(Math.floor(Math.abs(tz) / 60))
  const tzm = pad(Math.abs(tz) % 60)
  return `${y}-${m}-${d}T${hh}:${mm}:${ss}${sign}${tzh}:${tzm}`
}

/**
 * 입력값을 datetime-local 인풋에 맞는 로컬 문자열(YYYY-MM-DDTHH:mm)로 변환합니다.
 * - 타임존 포함 입력은 로컬로 변환
 * - 타임존 없는 naive ISO는 그대로 잘라서 반환
 * - 그 외 포맷은 Date 파싱 후 로컬로 변환
 * @param {string|number|Date} value
 * @returns {string} 빈 문자열 또는 YYYY-MM-DDTHH:mm
 */
export const toDatetimeLocalOrEmpty = (value) => {
  if (!value) return ''
  const s = String(value)
  const hasZone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(s)
  const isNaiveIso = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?$/.test(s)
  // 1) 타임존이 포함된 경우 → 로컬로 변환
  if (hasZone) {
    const date = new Date(s)
    if (Number.isNaN(date.getTime())) return ''
    const pad = (n) => String(n).padStart(2, '0')
    const y = date.getFullYear()
    const m = pad(date.getMonth() + 1)
    const d = pad(date.getDate())
    const hh = pad(date.getHours())
    const mm = pad(date.getMinutes())
    return `${y}-${m}-${d}T${hh}:${mm}`
  }
  // 2) 타임존이 없는 naive ISO 문자열 → 로컬로 가정하고 초만 제거
  if (isNaiveIso) {
    return s.slice(0, 16) // YYYY-MM-DDTHH:mm
  }
  // 3) 기타 포맷은 파싱 시도 후 로컬로 변환
  const date = new Date(s)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (n) => String(n).padStart(2, '0')
  const y = date.getFullYear()
  const m = pad(date.getMonth() + 1)
  const d = pad(date.getDate())
  const hh = pad(date.getHours())
  const mm = pad(date.getMinutes())
  return `${y}-${m}-${d}T${hh}:${mm}`
}

/**
 * 한국어 로캘로 날짜+시간을 포맷합니다.
 * @param {string|number|Date} value
 * @returns {string}
 */
export const formatKoreanDateTime = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString('ko-KR')
}

/**
 * 한국어 로캘로 날짜만 포맷합니다.
 * @param {string|number|Date} value
 * @returns {string}
 */
export const formatKoreanDate = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('ko-KR')
}

/**
 * 한국어 로캘로 시간(HH:mm)만 포맷합니다.
 * @param {string|number|Date} value
 * @returns {string}
 */
export const formatKoreanTime = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })
}

/**
 * 한국어 로캘로 시간 범위를 포맷합니다.
 * - 같은 날이면 "HH:mm - HH:mm"
 * - 다른 날이면 "M월 D일 HH:mm - M월 D일 HH:mm" 유사 표현
 * @param {string|number|Date} start
 * @param {string|number|Date} end
 * @returns {string}
 */
export const formatTimeRangeKorean = (start, end) => {
  if (!start || !end) return ''
  const s = new Date(start)
  const e = new Date(end)
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return ''
  const sameDay = s.toDateString() === e.toDateString()
  if (sameDay) return `${formatKoreanTime(s)} - ${formatKoreanTime(e)}`
  const sd = s.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })
  const ed = e.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })
  return `${sd} ${formatKoreanTime(s)} - ${ed} ${formatKoreanTime(e)}`
}

/**
 * UTC 시간과 IANA 타임존을 입력받아 해당 타임존의 현지 시간 ISO(YYYY-MM-DDTHH:mm:ss) 문자열을 반환합니다.
 * - 반환 문자열에는 타임존 오프셋을 포함하지 않습니다(표시용). 오프셋이 필요하면 별도 옵션/함수를 고려하세요.
 * - 브라우저의 Intl API를 사용하여 신뢰 가능한 타임존 변환을 수행합니다.
 * @param {string|number|Date} utcValue UTC 기준 시간값 (ISO 문자열, Date, epoch ms)
 * @param {string} timeZone IANA 타임존(ex. "Asia/Seoul", "America/New_York")
 * @returns {string} YYYY-MM-DDTHH:mm:ss (해당 타임존 현지 시각, 오프셋 미포함). 유효하지 않으면 빈 문자열.
 */
/**
 * UTC 시간값을 안전하게 Date 객체로 파싱합니다.
 * - 타임존 표시(Z 또는 ±HH:MM)가 없는 ISO 문자열은 UTC로 강제 해석합니다.
 *   (그렇지 않으면 브라우저가 로컬 타임존으로 해석하여 잘못된 epoch가 됩니다)
 * - Date 객체나 epoch 숫자는 그대로 Date 로 변환합니다.
 * @param {string|number|Date} value UTC 기준 시간값
 * @returns {Date|null} 유효하지 않으면 null
 */
export const parseUtc = (value) => {
  if (value == null || value === '') return null
  let v = value
  if (typeof value === 'string') {
    const hasZone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(value)
    v = hasZone ? value : value + 'Z'
  }
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? null : d
}

export const formatUtcToTimeZone = (utcValue, timeZone) => {
  if (!utcValue || !timeZone) return ''
  const date = parseUtc(utcValue)
  if (!date) return ''
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    })
    const parts = formatter.formatToParts(date).reduce((acc, p) => {
      acc[p.type] = p.value
      return acc
    }, {})
    // parts: { year, month, day, hour, minute, second }
    return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`
  } catch {
    return ''
  }
}

/**
 * 현지 시간과 IANA 타임존을 입력받아 UTC ISO 문자열을 반환합니다.
 * - formatUtcToTimeZone의 역변환 함수입니다.
 * - 시험 등록/수정 시 현지 시간을 UTC로 변환하여 서버에 전송할 때 사용합니다.
 * @param {string} localValue 현지 시간 문자열 (YYYY-MM-DDTHH:mm 또는 YYYY-MM-DDTHH:mm:ss)
 * @param {string} timeZone IANA 타임존(ex. "Asia/Seoul", "America/New_York")
 * @returns {string} UTC ISO 문자열 (YYYY-MM-DDTHH:mm:ssZ). 유효하지 않으면 빈 문자열.
 */
export const formatLocalToUtc = (localValue, timeZone) => {
  if (!localValue || !timeZone) return ''
  try {
    // 현지 시간 문자열 파싱
    const [datePart, timePart] = localValue.split('T')
    if (!datePart || !timePart) return ''

    const [year, month, day] = datePart.split('-').map(Number)
    const [hour, minute, second = 0] = timePart.split(':').map(n => parseInt(n, 10) || 0)

    // 해당 타임존의 오프셋을 계산하기 위해 임시 날짜 생성
    // Intl.DateTimeFormat을 사용하여 타임존 오프셋 추출
    const tempDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second))

    // 타임존의 현지 시간을 구함
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    })

    // UTC 시간을 해당 타임존으로 변환했을 때의 차이 계산
    const parts = formatter.formatToParts(tempDate).reduce((acc, p) => {
      acc[p.type] = parseInt(p.value, 10)
      return acc
    }, {})

    // 타임존 변환으로 인한 차이 계산
    const tzYear = parts.year
    const tzMonth = parts.month
    const tzDay = parts.day
    const tzHour = parts.hour === 24 ? 0 : parts.hour // 24시를 0시로 변환
    const tzMinute = parts.minute
    const tzSecond = parts.second

    // 원하는 현지 시간과 타임존 변환된 시간의 차이를 구함
    const targetLocal = new Date(year, month - 1, day, hour, minute, second)
    const convertedLocal = new Date(tzYear, tzMonth - 1, tzDay, tzHour, tzMinute, tzSecond)

    // 차이만큼 UTC 시간 조정
    const diffMs = targetLocal.getTime() - convertedLocal.getTime()
    const utcDate = new Date(tempDate.getTime() + diffMs)

    // UTC ISO 문자열 반환
    const pad = (n) => String(n).padStart(2, '0')
    return `${utcDate.getUTCFullYear()}-${pad(utcDate.getUTCMonth() + 1)}-${pad(utcDate.getUTCDate())}T${pad(utcDate.getUTCHours())}:${pad(utcDate.getUTCMinutes())}:${pad(utcDate.getUTCSeconds())}Z`
  } catch {
    return ''
  }
}

/**
 * 일반적으로 자주 사용하는 IANA 타임존 목록입니다.
 */
export const COMMON_TIMEZONES = [
  'UTC',
  'Asia/Seoul',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Hong_Kong',
  'Asia/Singapore',
  'Asia/Taipei',
  'Asia/Bangkok',
  'Asia/Kolkata',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Paris',
  'Europe/Madrid',
  'Europe/Moscow',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Sao_Paulo',
  'Australia/Sydney',
  'Pacific/Auckland'
]
/* ─── 학습활동 시각 (서버 UTC 전환 · 2026-09) ───────────────────────────────
 * 백엔드가 학습활동 시각을 기기 로컬 시각 대신 서버 UTC 로 기록하고 응답에
 * `+00:00` 오프셋을 붙인다 (docs/학습활동_시각표기_UTC전환.md).
 *   - 오프셋이 붙은 값 → UTC 로 파싱해 KST 로 환산
 *   - 오프셋이 없는 값 → 아직 전환 전(2단계 대상 필드·구 데이터)이라 KST 로 간주
 * 학생 리포트의 records[].date 가 KST 달력일로 고정되므로, 화면 표기도
 * 브라우저 로컬이 아닌 KST 로 통일해 한 화면 안에서 날짜가 어긋나지 않게 한다.
 */
export const ACADEMY_TIME_ZONE = 'Asia/Seoul'

/** 화면에 함께 노출하는 기준 시간대 라벨 */
export const ACADEMY_TIME_ZONE_LABEL = 'KST'

/**
 * 학습활동 시각값을 Date 로 파싱합니다.
 * @param {string|number|Date} value
 * @returns {Date|null} 유효하지 않으면 null
 */
export const parseActivityTime = (value) => {
  if (value == null || value === '') return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  if (typeof value === 'number') {
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? null : d
  }
  // 구 데이터의 "2026-03-31 15:50:45 KST" 형태(타임존 약어·공백 구분자)도 받아 준다
  let s = String(value).trim().replace(/\s+(KST|JST|UTC|GMT)$/i, '').replace(' ', 'T')
  if (!/[zZ]$|[+-]\d{2}:?\d{2}$/.test(s)) s += '+09:00' // 표기 없는 값 = KST
  const date = new Date(s)
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * 학습활동 시각을 정렬·비교용 epoch ms 로 변환합니다.
 * @param {string|number|Date} value
 * @returns {number|null}
 */
export const activityTimestamp = (value) => {
  const date = parseActivityTime(value)
  return date ? date.getTime() : null
}

/**
 * 학습활동 시각을 KST 날짜+시간으로 표기합니다. (예: 2026년 9월 6일 오후 06:49 KST)
 * - 시간대 라벨을 함께 붙입니다. 강사 계정이 7개국에 분포해(백엔드 회신 260911)
 *   라벨이 없으면 해외 강사가 자기 현지 시각으로 오해합니다.
 * @param {string|number|Date} value
 * @param {string} fallback 값이 없거나 파싱 실패 시 반환할 문자열
 * @returns {string}
 */
export const formatActivityDateTime = (value, fallback = '') => {
  const date = parseActivityTime(value)
  if (!date) return fallback
  const text = date.toLocaleString('ko-KR', {
    timeZone: ACADEMY_TIME_ZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
  return text + ' ' + ACADEMY_TIME_ZONE_LABEL
}

/**
 * 학습활동 시각을 KST 월·일로 표기합니다. (예: 9월 6일)
 * @param {string|number|Date} value
 * @param {string} fallback 값이 없거나 파싱 실패 시 반환할 문자열
 * @returns {string}
 */
export const formatActivityMonthDay = (value, fallback = '-') => {
  const date = parseActivityTime(value)
  if (!date) return fallback
  return date.toLocaleDateString('ko-KR', {
    timeZone: ACADEMY_TIME_ZONE,
    month: 'short',
    day: 'numeric'
  })
}
